import dayjs from 'dayjs';
import autoTable from 'jspdf-autotable';
import ReportCoreMixin from '@shared_mixins/report/report.core.mixin.js';
import {mapGetters, mapState} from 'vuex';
import background_texture_img from '@shared/assets/img/background_texture.png';
import QESGLogoWhite from '@shared/assets/img/QuantifiedESG_Logo_v3_White.png';
import ShortcutIcon from '@shared/assets/img/IcBaselineShortcut.png';
import {waitTime} from '@shared/utils/common';
import {
    mapCampaignIdToCompanySize,
    codeResult,
    rgbaToRgb,
    summaryMsg, convertSubCodeToCode, nexenWording
} from '@shared/mixin/report/raw-data/supply-2026';

const {localized, localizedLevelDesc, buildQuestionNameMap, isNoAnswerFormula, isUnknownAnswerFormula} = require('./report-supply-text.cjs');
const {isSelfAssessmentProject, isNexenProject} = require('./report-self-assessment.cjs');

export default {
    mixins: [
        ReportCoreMixin
    ],
    data() {
        return {
            report_colors: {
                primary: '#2C2A54',
                primary_rgb: [44, 42, 84],
                selected: '#3e3b7a',
                compliance: '#084f6a',
                compliance_rgb: [8, 79, 106],
                nonCompliance: '#c00000',
                nonCompliance_rgb: [192, 0, 0],
                secondary: '#e97132',
                warning: '#c04f15',
                error: '#D9534F',
                error_rgb: [217, 83, 79],
                gray: '#B0B0C0',
                muted: '#747373',
                state: {
                    A: [49, 133, 157],
                    B: [147, 204, 221],
                    C: [241, 220, 219],
                    D: [192, 80, 79],
                    N: [116, 115, 115],
                }
            },
            codeDoughnutChartInstance: null,
            codeDoughnutChartInstanceImg: null,
            
            mapSubCodeToScoringPd: {},
            auditData: {},
            linkDestinationInfo: {
                toImprovement : {},
                toAppendix: {},
            },
            // 아래 catch 가 렌더 예외를 삼키고 부분 문서를 반환하므로(그래서 백지 보고서가 배포됐다, d44c570)
            // 호출자가 "왜 실패했는지" 를 알 방법이 없었다. 삼키는 동작은 그대로 두고 원인만 밖으로 내보낸다.
            lastRenderError: null,
            overAllScore: {
                total: null,
                social: null,
                environment: null
            }
        };
    },
    computed: {
        ...mapState('evaluations', ['audit_category']),
        ...mapGetters('evaluations', ['get_audit_category_name']),
        headerInfo() {
            const _user_input_company_name = this.reportData.info.users.find(({criteria_name}) => criteria_name === '회사명');
            return {
                company_id: this.reportData?.info?.company.id,
                request_id: this.reportData?.info?.id,
                company_name: _user_input_company_name?.value || this.reportData?.info?.company?.company_name,
                vendor_code: this.reportData?.info?.vendor_code,
                published_date: this.reportData?.info?.completed_ts
            };
        },
        // DB 의 분류·Code명 국문→영문 맵 (results[].data 에는 국문 이름만 온다 — report-supply-text.cjs)
        questionNameEnMap() {
            return buildQuestionNameMap(this.reportData);
        },
        _jsPDF_headerStyles() {
            return {
                fontSize: 8.5,
                fillColor: [44, 42, 84],
                textColor: [255, 255, 255]
            };
        },
        companyInfo() {
            const _company_info = {
                '매출액': ' - ',
                '회사명': this.reportData.info.company.company_name,
                '기업규모': mapCampaignIdToCompanySize[this.reportData.info.campaign.id],
                '근로자수': ' - ',
                '근로자_정규직': ' - ',
                '근로자_계약직': ' - ',
                '근로자_일용직': ' - ',
            };
            if (!this.reportData?.parser_result?.values.length) {
                return _company_info;
            } else {
                this.reportData?.parser_result.values.forEach((info) => {
                    _company_info[info.criteria_name] = info.value.length ? info.value[0] : ' - ';
                });
                
                return _company_info;
            }
        },
        assessmentInfo() {
            let {
                service_type,
                requested_ts,
                in_progress_ts,
                in_assessment_ts,
                duedate_ts,
                completed_ts,
                project_code
            } = this.reportData.info;
            service_type = service_type === '일반' ? this.$t('일반 평가', this.report_lang) : this.$t('심화 평가', this.report_lang);
            
            let _assessmentInfo = {
                service_type: null, requested_date: requested_ts, duedate_date: duedate_ts, completed_date: completed_ts, project_code
            };
            
            if (service_type) _assessmentInfo.service_type = this.$t(service_type, this.report_lang);
            
            // 평가기간 실 평가기간으로 교체
            if (_assessmentInfo.requested_date) {
                _assessmentInfo.requested_date = this.report_lang === 'ko'
                    ? dayjs(_assessmentInfo.requested_date).format('YYYY년 MM월 DD일')
                    : dayjs(_assessmentInfo.requested_date).format('MM/DD/YYYY');
            } else {
                _assessmentInfo.requested_date = ' - ';
            }
            
            if (_assessmentInfo.duedate_date) {
                _assessmentInfo.duedate_date = this.report_lang === 'ko'
                    ? dayjs(_assessmentInfo.duedate_date).format('YYYY년 MM월 DD일')
                    : dayjs(_assessmentInfo.duedate_date).format('MM/DD/YYYY');
            } else {
                _assessmentInfo.duedate_date = ' - ';
            }
            
            if (_assessmentInfo.completed_date) {
                _assessmentInfo.completed_date = this.report_lang === 'ko'
                    ? dayjs(_assessmentInfo.completed_date).format('YYYY년 MM월 DD일')
                    : dayjs(_assessmentInfo.completed_date).format('MM/DD/YYYY');
            } else {
                _assessmentInfo.completed_date = dayjs().format(this.report_lang === 'ko' ? 'YYYY년 MM월 DD일' : 'MM/DD/YYYY');
            }
            
            return _assessmentInfo;
        },
        levelData(){
            if(this.reportData?.results?.length < 1) return {};
            const [_, total, category1, category2, category3 ] = this.reportData.results;
            
            const processedTotalCodeData = {
                compliance: 0,
                nonCompliance: 0,
                total: 0,
                A: 0,
                B: 0,
                C: 0,
                D: 0,
            };
            category3.data.forEach(cate3 => {
                if(cate3.category3_ev_code == null) return;
                if(cate3.category3_ev_code !== 'N' ){
                    processedTotalCodeData.total += 1;
                }
                if(cate3.category3_ev_code === 'A' || cate3.category3_ev_code === 'B'){
                    processedTotalCodeData.compliance += 1;
                    
                    if(cate3.category3_ev_code === 'A'){
                        processedTotalCodeData.A += 1;
                    }
                    if(cate3.category3_ev_code === 'B'){
                        processedTotalCodeData.B += 1;
                    }
                }
                if(cate3.category3_ev_code === 'C' || cate3.category3_ev_code === 'D'){
                    processedTotalCodeData.nonCompliance += 1;
                    
                    if(cate3.category3_ev_code === 'C'){
                        processedTotalCodeData.C += 1;
                    }
                    if(cate3.category3_ev_code === 'D'){
                        processedTotalCodeData.D += 1;
                    }
                }
            });
            
            
            return {
                total: {
                    ...total.data[0],
                    total_compliance: this.roundToOneDecimalPlace(total.data?.[0]?.total_compliance ?? 0)
                }, category1: category1.data, category2: category2.data , category3: category3.data, processedTotalCodeData
            };
        },
        subBaseData(){
            const unknownData = {
                total: 0,
                unknown: this.reportData.results[5].data[0].unknown_count,
                validRate: 0,
            };
            const mapSubCodeData = {};
            this.reportData.sources.sub_data.forEach(sub => {
                if (sub.sub_code.includes('ST')) return;
                if(sub.ev_code !== 'N') {
                    unknownData.total += 1;
                }
                mapSubCodeData[sub.sub_code] = sub;
            });
            unknownData.validRate = this.roundToOneDecimalPlace(((unknownData.total - unknownData.unknown) / unknownData.total) * 100);
            
            return {unknownData, mapSubCodeData};
        },
        evBaseData() {
            const mapEvData = {};
            const processedEvData = {};
            
            this.reportData.sources.raw_data.forEach(raw => {
                const currentCode = convertSubCodeToCode(raw.sub_code);
                if (mapEvData[currentCode] == null) {
                    mapEvData[currentCode] = {};
                }
                if (mapEvData[currentCode][raw.sub_code] == null) {
                    mapEvData[currentCode][raw.sub_code] = [];
                }
                mapEvData[currentCode][raw.sub_code].push(raw);
            });
            Object.entries(mapEvData).forEach(([key, subEvData]) => {
                processedEvData[key] = {};
                Object.entries(subEvData).forEach(([subCode, items]) => {
                    processedEvData[key][subCode] = {
                        isNA: false
                    };
                    const targetSub =  this.subBaseData.mapSubCodeData[subCode];
                    
                    // 문항이 해당 없음의 경우
                    if(targetSub.ev_code == null || targetSub.ev_code === 'N') {
                        processedEvData[key][subCode].isNA = true;
                        return;
                    }
                    
                    // 문항이 해당 없음이 아닌 경우
                    let selectedResultNoEv = null;
                    let selectedUnknownEv = null;
                    let  resultNoEv = null;
                    const removeIdx = [];
                    // formula 표기는 캠페인마다 다르다(`"아니오" == result`, `result=='아니오'`, `result=='모름 또는 확인할 수 없음'`)
                    // — 문자열 완전일치로 고르면 새 Code 의 아니오/모름 행이 하위 평가 요소로 새어 Appendix 렌더가 죽는다.
                    items.forEach((item,idx) => {
                        if(isNoAnswerFormula(item.formula)){
                            removeIdx.push(idx);
                            resultNoEv = item;
                            if(item.satisfied === true && item.not_applicable === false){
                                selectedResultNoEv = item;
                            }
                        }
                        if(isUnknownAnswerFormula(item.formula)) {
                            removeIdx.push(idx);
                            if(item.satisfied === true && item.not_applicable === false){
                                selectedUnknownEv = item;
                            }
                        }
                    });
                    // 아니오, 모름 평가지표 제거
                    const processedEvList = items.filter((_, idx) => !removeIdx.includes(idx));
                    // 예 하위지표 있는지 체크
                    const noLowerEv = items.every((item) => item.neg_ev_code == null);
                    
                    // 우선순위: 1) 개별 audit 카테고리(있을 때)  2) 부모 cascade(EV_NOT_NA 포함)  3) EV_AUTO
                    // null이 그대로 저장되면 Appendix에서 placeholder('평가 분류를 선택해주세요.')가 표시되므로 항상 fallback 보장
                    const getNegEv = (item, audit_category = null) => ({
                        ev_code: item.neg_ev_code.split('::')[0],
                        judged_by: item.judged_by,
                        comment: {ko: item.comment, ea: item.comment_ea, ca: item.comment_ca,},
                        audit_category_code: (item.judged_by === 'audit' ? item.audit_category_code : null)
                            ?? audit_category
                            ?? 'EV_AUTO',
                        ev_name:  {ko: item.reverse_name, ea: item.reverse_name_ea, ca: item.reverse_name_ca}
                    });
                    // "result = 아니오" 인 경우
                    if(selectedResultNoEv != null){
                        
                        let auditCategory = (selectedResultNoEv.judged_by === 'audit' ? selectedResultNoEv.audit_category_code : null) ?? 'EV_AUTO';
                        // 사용자가 해당 없음을 선택했으나 근거가 인정되지 않아 아니오로 처리된 경우, auditCategory custom 변경
                        if(selectedResultNoEv.origin_ev_code === 'N' &&  selectedResultNoEv.satisfied === true){
                            auditCategory = 'EV_NOT_NA';
                        }
                        // "예" 하위 평가 지표가 없는 경우 또는 B321 문항의 경우
                        if(noLowerEv || subCode === 'B3-2.1'){
                            processedEvData[key][subCode][selectedResultNoEv.ev_id] =  {
                                ev_code: selectedResultNoEv.ev_code,
                                judged_by: selectedResultNoEv.judged_by,
                                comment: {ko: selectedResultNoEv.comment, ea: selectedResultNoEv.comment_ea, ca: selectedResultNoEv.comment_ca,},
                                audit_category_code: auditCategory,
                                ev_name:  {ko: selectedResultNoEv.name, ea: selectedResultNoEv.name_ea, ca: selectedResultNoEv.name_ca,}
                            };
                        }
                        // "예" 하위 평가 지표가 있는 경우
                        else {
                            processedEvList.forEach((item) => {
                                processedEvData[key][subCode][item.ev_id] = getNegEv(item, auditCategory);
                            });
                        }
                    }
                    // "result = 모름" 인 경우
                    else if(selectedUnknownEv != null){
                        let auditCategory = (selectedUnknownEv.judged_by === 'audit' ? selectedUnknownEv.audit_category_code : null) ?? 'EV_AUTO';
                        // 사용자가 해당 없음을 선택했으나 근거가 인정되지 않아 아니오로 처리된 경우, auditCategory custom 변경
                        if(selectedUnknownEv.origin_ev_code === 'N' &&  selectedUnknownEv.satisfied === true){
                            auditCategory = 'EV_NOT_NA';
                        }
                        
                        // "예" 하위 평가 지표가 없는 경우 또는 B321 문항의 경우
                        if(noLowerEv || subCode === 'B3-2.1'){
                            processedEvData[key][subCode][selectedUnknownEv.ev_id] =  {
                                ev_code: resultNoEv.pos_ev_code, // 모름 수준 === 아니오 수준
                                judged_by: selectedUnknownEv.judged_by,
                                comment: {ko: selectedUnknownEv.comment, ea: selectedUnknownEv.comment_ea, ca: selectedUnknownEv.comment_ca,},
                                audit_category_code: auditCategory,
                                ev_name:  {ko: selectedUnknownEv.name, ea: selectedUnknownEv.name_ea, ca: selectedUnknownEv.name_ca,}
                            };
                        }
                        // "예" 하위 평가 지표가 있는 경우
                        else {
                            processedEvList.forEach((item) => {
                                processedEvData[key][subCode][item.ev_id] = getNegEv(item, auditCategory);
                            });
                        }
                    }
                    // "result = 예" 인 경우
                    else {
                        if(noLowerEv){
                            const selectedResultYesEv = processedEvList.find(item => item.satisfied === true && item.not_applicable === false);
                            processedEvData[key][subCode][selectedResultYesEv.ev_id] = {
                                ev_code: selectedResultYesEv.ev_code,
                                judged_by: selectedResultYesEv.judged_by,
                                comment: {ko: selectedResultYesEv.comment, ea: selectedResultYesEv.comment_ea, ca: selectedResultYesEv.comment_ca,},
                                audit_category_code: selectedResultYesEv.audit_category_code,
                                ev_name:  {ko: selectedResultYesEv.name, ea: selectedResultYesEv.name_ea, ca: selectedResultYesEv.name_ca,}
                            };
                            // audit이 아니거나 audit이 카테고리를 선택하지 않은 경우만 EV_AUTO 처리
                            // (auditor가 명시적으로 분류/코멘트를 남긴 경우 origin_ev_code === ev_code 여도 그 audit 정보 보존)
                            if(selectedResultYesEv.judged_by !== 'audit' || selectedResultYesEv.audit_category_code == null) {
                                processedEvData[key][subCode][selectedResultYesEv.ev_id].audit_category_code = 'EV_AUTO';
                            }
                        }
                        else {
                            processedEvList.forEach((item) => {
                                processedEvData[key][subCode][item.ev_id] = {
                                    ev_code: item.ev_code,
                                    judged_by: item.judged_by,
                                    comment: {ko: item.comment, ea: item.comment_ea, ca: item.comment_ca,},
                                    audit_category_code: item.audit_category_code,
                                    ev_name:  {ko: item.name, ea: item.name_ea, ca: item.name_ca,}
                                };
                                if(item.satisfied === false){
                                    processedEvData[key][subCode][item.ev_id].ev_name = {ko: item.reverse_name, ea: item.reverse_name_ea, ca: item.reverse_name_ca};
                                }
                                // audit이 아니거나 audit이 카테고리를 선택하지 않은 경우만 EV_AUTO 처리
                                // (auditor가 명시적으로 분류/코멘트를 남긴 경우 origin_ev_code === ev_code 여도 그 audit 정보 보존)
                                if(item.judged_by !== 'audit' || item.audit_category_code == null) {
                                    processedEvData[key][subCode][item.ev_id].audit_category_code = 'EV_AUTO';
                                }
                            });
                        }
                        
                    }
                    Object.keys(processedEvData).forEach(key => {
                        const sortedEntries = Object.keys(processedEvData[key])
                            .sort((a, b) => a.localeCompare(b))
                            .reduce((acc, subCode) => {
                                acc[subCode] = processedEvData[key][subCode];
                                return acc;
                            }, {});
                        processedEvData[key] = sortedEntries;
                    });
                });
            });
            
            return {mapEvData, processedEvData};
        }
    },
    watch: {},
    created() {
    },
    methods: {
        async generatePDF_supply(doc, position_top) {
            this.lastRenderError = null;
            try {
                this.pageContentWidth = doc.internal.pageSize.width - (this.jsPDFOpt.margin.left + this.jsPDFOpt.margin.right);
                this.pageContentHeight = doc.internal.pageSize.height - (this.jsPDFOpt.margin.top + this.jsPDFOpt.margin.bottom);
                
                this.generateCover_supply(doc);
                
                this.addDocPage(doc);
                this.addHeader(doc, 2);
                
                let position_y = await this.createSummaryPage_v4(doc, position_top);
                position_y = await this.createCodeSummaryPage(doc, position_y);
                position_y = await this.createCodeResultPage(doc, position_y);
                
                if (this.reportMode === 'full') {
                    position_y = await this.createAppendixPage(doc, position_y);
                }
                
                const totalPages = doc.getNumberOfPages();
                for (let i = 2; i <= totalPages; i++) {
                    doc.setPage(i);  // 현재 페이지로 이동
                    this.addFooter(doc);
                }
                
                doc.setDocumentProperties({
                    title: this.pdf_filename,
                    subject: this.pdf_filename,
                    filename: `${this.pdf_filename}.pdf`,
                    author: 'QuantifiedESG Inc.',
                    creator: 'QESG'
                });
            } catch (e) {
                console.error('err', e);
                console.dir(e);
                this.lastRenderError = e;
            }


            return doc;
        },
        addFooter (doc) {
            // this.setFont(doc)
            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setFontSize(this.jsPDFOpt.footerStyles.fontSize);
            doc.setFillColor(...this.jsPDFOpt.footerStyles.fillColor);
            
            const pageHeight = doc.internal.pageSize.getHeight();
            const [footerBox_position_x, footerBox_position_y] = [0, pageHeight - this.jsPDFOpt.footerHeight];
            const footerBox_width = doc.internal.pageSize.getWidth();
            doc.rect(footerBox_position_x, footerBox_position_y, footerBox_width, this.jsPDFOpt.footerHeight, 'F');
            
            // const _page_total = doc.internal.getNumberOfPages();
            const _page_total = doc.getNumberOfPages() - 1;
            const _page_current = doc.getCurrentPageInfo().pageNumber;
            
            // (좌측) QESG 로고
            const logo_max_height = 15;
            const logo_QESG_width = (40 * logo_max_height) / 10;
            const logo_QESG_position_y = pageHeight - (this.jsPDFOpt.footerHeight / 2) - 7.5;
            doc.addImage(this.qesg_logo_png, 'png', this.jsPDFOpt.margin.left, logo_QESG_position_y, logo_QESG_width, logo_max_height);

            // (우측) 페이지 넘버
            doc.setFontSize(10);
            const _footer_pages = doc.splitTextToSize(`${_page_current - 1} / ${_page_total}`, footerBox_width - this.jsPDFOpt.margin.right);
            const _footer_pages_height = doc.getTextDimensions(_footer_pages).h;
            const _footer_pages_width = doc.getTextDimensions(_footer_pages).w;
            const _footer_pages_position_x = footerBox_width - _footer_pages_width - (this.jsPDFOpt.margin.left);
            const _footer_pages_position_y = footerBox_position_y + (this.jsPDFOpt.footerHeight / 2) + (_footer_pages_height / 2);
            doc.text(_footer_pages, _footer_pages_position_x, _footer_pages_position_y);
        },
        async createSummaryPage_v4(doc, position_y) {
            position_y = this.firstElementSpacing;
            position_y = this.addTitle(doc, position_y, 1, 'ESG 평가 결과 요약');
            position_y = this.summary_processCompanyInfo(doc, position_y);
            
            const {
                chartXPos,
                rightContentXPos,
                position_y: after_position_y
            } = await this.summary_processCodeCompliance(doc, position_y);
            position_y = after_position_y;
            position_y = await this.summary_processUnknown(doc, position_y, chartXPos, rightContentXPos);
            
            return position_y;
        },
        async createCodeSummaryPage(doc, position_y) {
            this.addDocPage(doc);
            this.addHeader(doc, 2);
            
            position_y = this.firstElementSpacing;
            position_y = await this.codeSummary_AreaCompliance(doc, position_y);
            position_y = await this.codeSummary_CodeCompliance(doc, position_y);
            
            return position_y;
        },
        async createCodeResultPage(doc, position_y) {
            for (const codeResult of this.reportData.results[4].data) {
                if (codeResult.category3_question_name.includes('기본')) continue;
                this.addDocPage(doc);
                this.addHeader(doc, 2);
                
                position_y = this.firstElementSpacing;
                position_y = this.addTitle(doc, position_y, 1, this.$t('코드별 평가결과 요약', this.report_lang));
                position_y = await this.codeResult_summary(doc, position_y, codeResult);
                position_y = await this.codeResult_scoreCard(doc, position_y, codeResult);
                position_y = await this.codeResult_nonComplianceDetail(doc, position_y, codeResult);
            }
            
            return position_y;
        },
        async createAppendixPage(doc, position_y) {
            this.addDocPage(doc);
            this.addHeader(doc, 2);
            
            const leftStart = this.jsPDFOpt.margin.left;
            const isEn = this.report_lang === 'en';
            // DB 의 평가 요소명·코멘트는 {ko, ea, ca} 로 온다 — 보고서 언어 'en' 은 'ea' 로 읽어야 영문이 나온다
            const dbLangKey = isEn ? 'ea' : this.report_lang;

            position_y = this.firstElementSpacing;
            // 영문은 어순이 달라 조각을 이어 붙일 수 없다
            const title = isEn
                ? `Appendix. Responses of ${this.companyInfo.회사명} and Assessment Rationale`
                : `Appendix ${this.companyInfo.회사명} ${this.$t('답변과 평가 근거', this.report_lang)}`;
            position_y = this.addTitle(doc, position_y, 1, title);
            
            let cate3Idx = 0;
            for (const cate3 of this.reportData.results[4].data) {
                cate3Idx += 1;
                if (cate3.category3_question_name.includes('기본')) continue;
                
                const code = cate3.category3_question_name.split('.')[0];
                const targetCodeEvData =  this.evBaseData.processedEvData[code] || {};
                
              
                const codeTitle = `Code ${this.questionNameOf(cate3.category3_question_name)}`;
                position_y = this.addTitle(doc, position_y, 2, codeTitle, {skip_divider: true});
                
                
                const entriesTargetCodeEvData =  Object.entries(targetCodeEvData);
                entriesTargetCodeEvData.forEach(([subCode, evItems], idx) => {
                    const checkLast = idx === entriesTargetCodeEvData.length -1 && cate3Idx === this.reportData.results[4].data.length;
                    const targetSub = this.subBaseData.mapSubCodeData[subCode];
                    const targetCodeLevelDesc = this.reportData.ev_code_list.find(item => item.code === (targetSub.ev_code ?? 'N')).description;
                    const subEvCode = targetSub.ev_code;
                    const isSubNonCompliance = (subEvCode === 'D' || subEvCode === 'C');
                    
                    const subTitle = `[${targetSub.label}] ${this.$t('문항 수준', this.report_lang)}: ${this.$t(targetCodeLevelDesc, this.report_lang)}`;
                    const subTitleYPos = position_y + 4;
                    this.setFont(doc, 'table_td', null, 'medium');
                    doc.setTextColor(29, 29, 29);
                    doc.setFontSize(11.5);
                    doc.text(subTitle, leftStart, subTitleYPos);

                    position_y += 8;
                    // 영문은 문항 제목과 범례가 둘 다 길어 같은 줄에서 겹친다 → 범례를 한 줄 아래로
                    if (isEn && !evItems.isNA) position_y += 9;
                    
                    if(evItems.isNA){
                        const emptyCardYPos = position_y + 2;
                        doc.setDrawColor('#c2bfbf');
                        doc.setLineWidth(0.6);
                        doc.setFillColor(...rgbaToRgb([...this.report_colors.state.N, 0.25]));
                        doc.rect(leftStart, emptyCardYPos, this.pageContentWidth, 20, 'FD');
                        this.setFont(doc, 'table_td', null, 'medium');
                        doc.setFontSize(11);
                        doc.setTextColor('#666666');
                        doc.text('-', leftStart + this.pageContentWidth / 2, emptyCardYPos + 13, {align: 'center'});
                        
                        position_y += 36;
                        
                        
                        
                        
                        if (!checkLast) {
                            if(this.checkNeedNewPage(doc, position_y, 70)){
                                this.addDocPage(doc, true);
                                this.addHeader(doc, 2);
                                position_y = this.firstElementSpacing;
                            }
                        }
                        
                        return;
                    }
                    
                    const legendList =  [
                        {value: this.$t('매우 미흡', this.report_lang), color: this.report_colors.state.D},
                        {value: this.$t('미흡', this.report_lang), color: this.report_colors.state.C},
                        {value: this.$t('보통', this.report_lang), color: this.report_colors.state.B},
                        {value: this.$t('우수', this.report_lang), color: this.report_colors.state.A}
                    ];
                    
                    this.setFont(doc, 'table_td', null, 'normal');
                    doc.setTextColor('#666666');
                    doc.setFontSize(9);
                    
                    let baseXPos = leftStart + this.pageContentWidth;
                    legendList.forEach(({value, color}, index) => {
                        const legendMsg = doc.splitTextToSize(value, this.pageContentWidth);
                        const legendMsgDim = doc.getTextDimensions(legendMsg);
                        const legendPosX = baseXPos - legendMsgDim.w;
                        doc.text(legendMsg, legendPosX, position_y + 2);
                        
                        // 첫 번째가 아니면 쉼표 추가
                        const textWithComma = index > 0 ? value + ',' : value;
                        doc.text(textWithComma, legendPosX, position_y + 2);
                        
                        // 원형 태그 위치 계산
                        const circleRadius = 3;
                        const circleX = legendPosX - circleRadius - 4; // 텍스트 왼쪽에 원 배치
                        const circleY = position_y;
                        
                        // 원형 배경 (border 없음)
                        doc.setDrawColor('#232323');
                        doc.setLineWidth(1);
                        doc.setFillColor(...color);
                        doc.circle(circleX, circleY, circleRadius, 'F');
                        
                        baseXPos = circleX - circleRadius - 8; // 다음 항목 시작 위치
                    });
                    
                    this.setFont(doc, 'table_td', null, 'normal');
                    doc.setTextColor('#666666');
                    doc.setFontSize(9);
                    const legendLabel = this.$t('범례', this.report_lang) + ':';
                    // 국문은 기존 위치 그대로. 영문 라벨은 더 길어 첫 원과 겹치므로 글자 폭만큼 왼쪽에서 시작
                    doc.text(legendLabel, isEn ? baseXPos + 4 - doc.getTextWidth(legendLabel) : baseXPos - 12, position_y + 2);


                    position_y += 10;
                    
                    const thStyle = {
                        ...this.tableStyles.th,
                        lineColor: '#232323',
                        halign: 'center',
                        fontStyle: 'bold',
                    };
                    
                    const tableBody = [
                        [
                            {
                                content: this.$t('수준', this.report_lang),
                                styles: {...thStyle }
                            },
                            {
                                content: this.$t('평가 요소', this.report_lang),
                                colSpan: 2,
                                styles: {...thStyle }
                            },
                            {
                                content: this.$t('평가 근거 ', this.report_lang),
                                colSpan: 2,
                                styles: {...thStyle }
                            },
                        ]
                    ];

                    Object.entries(evItems).forEach(([ev_id, evItem]) => {
                        if(ev_id === 'isNA') return;
                        
                        const evName = evItem.ev_name[dbLangKey] || (evItem.ev_name['ko'] ?? '-');
                        const evComment = evItem.comment[dbLangKey] || (evItem.comment['ko'] ?? '-');
                        const ev_code = evItem.ev_code;
                        let auditCategory = evItem.audit_category_code;
                        
                        
                        const isNonCompliance = (ev_code === 'D' || ev_code === 'C');
                        
                        const tdStyle = {
                            ...this.tableStyles.td,
                            lineColor: '#232323',
                            halign: 'center',
                            fontStyle: 'normal',
                        };
                        
                        const target_audit_category = this.audit_category.find(cat => cat.code === auditCategory);
                        
                        if(evItem.audit_category_code === 'EV_AUTO'){
                            auditCategory = '';
                        }
                        else if(evItem.audit_category_code === 'EV_NOT_NA'){
                            auditCategory = this.$t('해당없음 사유 인정 불가', this.report_lang);
                        }
                        else if(evItem.audit_category_code === 'EV_NOBASE'){
                            auditCategory = this.$t('제출 증빙 내 근거 불충분', this.report_lang);
                        }
                        else {
                            auditCategory = this.report_lang === 'ko' ? target_audit_category?.category : this.report_lang === 'en' ? target_audit_category?.category_ea : target_audit_category?.category_ca;
                        }
                        
                        tableBody.push([
                            {
                                content: '',
                                ev_code,
                                styles: {...tdStyle },
                            },
                            {
                                content: evName,
                                styles: {...tdStyle, halign: evName === '-' ? 'center' : 'left'  },
                                colSpan: isNonCompliance ? 1 : 2,
                            },
                            {
                                content: evItem.audit_category_code === 'EV_AUTO' ? '-' : auditCategory,
                                colSpan: evItem.audit_category_code === 'EV_AUTO' ? 2 : 1,
                                styles: {...tdStyle },
                            },
                            {
                                content: evComment,
                                styles: {...tdStyle, halign: evComment === '-' ? 'center' : 'left' },
                            }
                        ]);
                        if(isNonCompliance){
                            tableBody[tableBody.length -1].splice(2, 0, {
                                content: '',
                                styles: {...tdStyle, cellWidth: 16 },
                                data: {
                                    ev_id, subCode, code,
                                },
                                type: 'link'
                            } );
                        }
                    });
                    
                    let leftCellWidth = this.pageContentWidth * 0.5;
                    const rightCellWidth = this.pageContentWidth * 0.5;
                    
                    // 영문 헤더 'Level' 은 24 에 안 들어가 글자 중간에서 줄바꿈된다
                    const levelColWidth = isEn ? 32 : 24;
                    const columnStyles = [
                        { cellWidth: levelColWidth },
                        { cellWidth: leftCellWidth - 16 - levelColWidth },
                        { cellWidth: 16 },
                        { cellWidth: 60 },
                        { cellWidth: rightCellWidth - 60 }
                    ];
                    
                    autoTable(doc, {
                        theme: 'grid',
                        startY: position_y,
                        margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing - 5, bottom: 45},
                        tableWidth: this.pageContentWidth,
                        columnStyles:columnStyles,
                        pageBreak: 'auto',
                        rowPageBreak: 'avoid',
                        body: tableBody,
                        willDrawCell: (data) => {
                            if (data.section === 'body') {
                                const {cell} = data;
                                
                                if (cell.raw?.ev_code != null) {
                                    cell.text = [];
                                }
                            }
                        },
                        didDrawCell: (data) => {
                            // 첫 번째 열(수준)이고 헤더가 아닌 경우
                            if (data.column.index === 0 && data.row.index > 0) {
                                const doc = data.doc;
                                const ev_code = data.cell.raw.ev_code; // 'D', 'C', 'B', 'A'
                                // 색이 없는 등급(null/U 등)은 태그를 건너뛴다 — 장식 훅의 throw 는 catch 에 삼켜져 이후 렌더가 통째로 빠진다
                                const circleColor = this.report_colors.state[ev_code];
                                if (!circleColor) return;

                                // 원형 태그 위치 계산
                                const circleRadius = 3;
                                const circleX = data.cell.x + data.cell.width / 2;
                                const circleY = data.cell.y + data.cell.height / 2;

                                // 원형 배경 (border 없음)
                                doc.setLineWidth(0);
                                doc.setFillColor(...circleColor);
                                doc.circle(circleX, circleY, circleRadius, 'F');
                            }
                            if (data.column.index === 2 && data.row.index > 0 && data.cell.raw?.type === 'link') {
                                const {cell} = data;
                                const {ev_id} = cell.raw.data;
                                const doc = data.doc;
                                const iconSize = 8;
                                const iconX = data.cell.x + (data.cell.width - iconSize) / 2;
                                const iconY = data.cell.y + (data.cell.height - iconSize) / 2;
                                
                                doc.addImage(ShortcutIcon, 'SVG', iconX, iconY, iconSize, iconSize);
                                
                                let positionY = cell.y - 60;
                                if(cell.y - 60 < this.jsPDFOpt.margin.top){
                                    positionY = this.jsPDFOpt.margin.top;
                                }
                                this.linkDestinationInfo.toAppendix[ev_id].linkDestination = {
                                    pageNumber: doc.internal.getCurrentPageInfo().pageNumber,
                                    y: positionY
                                };
                                this.linkDestinationInfo.toImprovement[ev_id].linkSource = {
                                    pageNumber: doc.internal.getCurrentPageInfo().pageNumber,
                                    x: cell.x,
                                    y: cell.y,
                                    width: cell.width,
                                    height: cell.height
                                };
                            }
                        },
                        didDrawPage: (HookData) => {
                            this.addHeader(HookData.doc);
                        },
                    });
                   
                    if(isSubNonCompliance){
                        position_y = doc.lastAutoTable.finalY + 6;
                        
                        let baseGuideXPos = leftStart + this.pageContentWidth;
                        const iconSize = 8;
                        const iconY = position_y - 4;
                        this.setFont(doc, 'table_td', null, 'normal');
                        doc.setTextColor('#666666');
                        doc.setFontSize(8.5);
                        const iconDescText = `: ${this.$t('클릭 시, 해당 평가 요소의 개선 권고사항을 확인하실 수 있습니다.', this.report_lang)}`;
                        position_y += this.drawIconDesc(doc, iconDescText, baseGuideXPos, position_y, iconSize, iconY);

                        position_y += 12 + this.jsPDFOpt.spacing.section_xs;
                    }
                    else {
                        position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
                    }
                        
                    if(idx !== entriesTargetCodeEvData.length - 1 || !checkLast) {
                        if (this.checkNeedNewPage(doc, position_y, 70)) {
                            this.addDocPage(doc, true);
                            this.addHeader(doc);
                            position_y = this.firstElementSpacing;
                        }
                    }
                });
            }
            
            ['toAppendix', 'toImprovement'].forEach((key) => {
                Object.values(this.linkDestinationInfo[key]).forEach(({linkSource, linkDestination}) => {
                    if (linkSource && linkDestination) {
                        // 링크를 그려야 할 출발지 페이지로 다시 이동
                        doc.setPage(linkSource.pageNumber);
                        
                        // 저장해둔 정보를 바탕으로 링크 생성
                        doc.link(
                            linkSource.x,
                            linkSource.y,
                            linkSource.width,
                            linkSource.height,
                            {
                                pageNumber: linkDestination.pageNumber,
                                top: linkDestination.y
                            }
                        );
                    }
                });
            });
            
            return position_y;
        },
        // SAQ 2026 하드코딩 맵에 없는 코드(예: 넥센 전용 C-4-1)를 만나도 생성이 죽지 않게 하는 폴백.
        // 맵에 있으면 정식 문구 그대로, 없으면 문항명 잔여부/생략/'-' 로 우아하게 강등된다 (2026-09-03 넥센 빈 2쪽 장애).
        // 영문 보고서면 영문 문구, 영문이 없는 항목은 항목 단위로 국문 폴백 (report-supply-text.cjs).
        codeConductOf(code, questionName) {
            const conduct = codeResult.mapCodeToConduct[code];
            if (conduct) return this.nexenWordingOf('conduct', code, localized(this.report_lang, conduct, 'conduct', code));
            return String(this.questionNameOf(questionName) || '').split('.').slice(1).join('.').trim()
                || code;
        },
        codeConductDescOf(code) {
            return this.nexenWordingOf('conductDesc', code, localized(this.report_lang, codeResult.mapCodeToConductDesc[code] || '', 'conductDesc', code));
        },
        // 넥센(NEXTIER-*, 자가진단 포함) 보고서는 철강 업종 전제 구절을 업종 중립 문구로 바꾼다 (supply-2026.js NEXEN_WORDING).
        nexenWordingOf(kind, code, text) {
            return isNexenProject(this.reportData?.info?.project_code) ? nexenWording(this.report_lang, kind, code, text) : text;
        },
        codeLevelDescOf(code) {
            return localizedLevelDesc(this.report_lang, codeResult.mapCodeLevelDesc[code] || {A: '-', B: '-', C: '-', D: '-'}, code);
        },
        // 표시용 분류·Code명: 영문 보고서면 DB 영문(없으면 국문).
        // ⛔ Code 추출(`.split('.')[0]`)과 '기본' 제외 판정은 계속 국문 이름으로 한다 — 영문 이름에는 같은 규칙이 보장되지 않는다.
        questionNameOf(koName) {
            return (this.report_lang === 'en' && this.questionNameEnMap[koName]) || koName;
        },
        // 오른쪽 정렬 아이콘 안내문. 영문은 한 줄에 안 들어갈 수 있어 줄바꿈하고, 늘어난 높이를 돌려준다 (한 줄이면 0).
        drawIconDesc(doc, text, rightX, position_y, iconSize, iconY) {
            const lines = doc.splitTextToSize(text, this.pageContentWidth - iconSize - 4);
            const dim = doc.getTextDimensions(lines);
            const textX = rightX - dim.w;
            doc.text(lines, textX, position_y + 3);
            doc.addImage(ShortcutIcon, 'SVG', textX - iconSize - 2, iconY, iconSize, iconSize);
            return lines.length > 1 ? dim.h - dim.h / lines.length : 0;
        },
        async codeResult_summary(doc, position_y, cate3) {
            const leftStart = this.jsPDFOpt.margin.left;
            const codeTextCardYPos = position_y + 4;
            const isEn = this.report_lang === 'en';
            const code = cate3.category3_question_name.split('.')[0];
            const codeTagXPos = leftStart + 6;
            const codeConductXPos = codeTagXPos + 40 + 8;
            const codeConductWidth = this.pageContentWidth - codeConductXPos + 24;
            const codeConductRaw = this.codeConductOf(code, cate3.category3_question_name);

            // 제목 줄 수를 카드를 그리기 전에 잰다. 2줄 이하는 기존 높이(30) 그대로이고,
            // 영문처럼 3줄 이상이면 글자를 한 단계 더 줄이고 넘는 줄만큼 카드를 늘린다.
            this.setFont(doc, 'table_td', null, 'bold');
            let codeConductFontSize = 13;
            doc.setFontSize(codeConductFontSize);
            let codeConductText = doc.splitTextToSize(codeConductRaw, codeConductWidth);
            if (codeConductText.length > 1) {
                codeConductFontSize = 12;
                doc.setFontSize(codeConductFontSize);
                codeConductText = doc.splitTextToSize(codeConductRaw, codeConductWidth);
            }
            if (codeConductText.length > 2) {
                codeConductFontSize = 11;
                doc.setFontSize(codeConductFontSize);
                codeConductText = doc.splitTextToSize(codeConductRaw, codeConductWidth);
            }
            const codeConductLineHeight = doc.getTextDimensions(codeConductText).h / codeConductText.length;
            const cardExtra = Math.max(0, codeConductText.length - 2) * codeConductLineHeight;

            doc.setDrawColor('#c2bfbf');
            doc.setLineWidth(1);
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.rect(leftStart, codeTextCardYPos, this.pageContentWidth, 30 + cardExtra, 'FD');

            doc.setLineWidth(0);
            doc.setFillColor(this.report_colors.muted);
            doc.roundedRect(codeTagXPos, codeTextCardYPos + 6, 40, 19, 4, 4, 'F');

            this.setFont(doc, 'table_td', null, 'bold');
            doc.setFontSize(15);
            doc.setTextColor('#ffffff');
            doc.text(code, codeTagXPos + 20, codeTextCardYPos + 20, {align: 'center'});

            const codeConductYPos = codeTextCardYPos + 19 - (codeConductText.length > 1 ? 6 : 0);
            doc.setTextColor('#232323');
            doc.setFontSize(codeConductFontSize);
            doc.text(codeConductText, codeConductXPos, codeConductYPos, {align: 'left'});


            const conductDescText = this.codeConductDescOf(code);
            let conductDescCardHeight = 0;
            if (conductDescText) {
                const conductDescCardYPos = position_y + 44 + cardExtra;
                this.setFont(doc, 'table_td', null, 'normal');
                doc.setTextColor('#232323');
                doc.setFontSize(10.5);

                const conductDescMsg = doc.splitTextToSize(conductDescText, this.pageContentWidth - 10);
                const conductDescMsgDim = doc.getTextDimensions(conductDescMsg);
                conductDescCardHeight = conductDescMsgDim.h + 16;

                doc.setFillColor(...this.jsPDFOpt.card.fillColor);
                doc.setDrawColor('#c2bfbf');
                doc.setLineWidth(0.5);
                doc.rect(leftStart, conductDescCardYPos, this.pageContentWidth, conductDescCardHeight, 'FD');
                doc.text(conductDescMsg, leftStart + 8, conductDescCardYPos + 14, {align: 'left'});
            }
            
            position_y += conductDescCardHeight + 50 + cardExtra;

            const targetCodeLevelDesc = this.reportData.ev_code_list.find(item => item.code === (cate3.category3_ev_code ?? 'N')).description;
            const gradeLabel = this.$t(targetCodeLevelDesc, this.report_lang);
            const guideMsgText = codeResult.mapCodeComplianceMsg({
                locale: this.report_lang,
                company_name: this.companyInfo['회사명'],
                code,
                ev_code_desc: isEn ? gradeLabel : targetCodeLevelDesc,
                ev_code: cate3.category3_ev_code ?? 'N',
                code_desc: this.questionNameOf(cate3.category3_question_name),
            });
            // 국문은 기존 수치(카드 62, 구분선 128, 등급 태그 58) 그대로.
            // 영문은 등급 라벨('Critical Laggard')과 판정 문장이 길어 태그 폭·구분선 위치·카드 높이를 글자에 맞춘다.
            let codeResultCardHeight = 62;
            let lineXPos = 128;
            let codeLevelTagWidth = 58;
            let codeLevelGuideMsg = null;
            let codeLevelGuideMsgDim = null;
            if (isEn) {
                this.setFont(doc, 'table_td', null, 'bold');
                doc.setFontSize(12);
                codeLevelTagWidth = Math.max(58, doc.getTextWidth(gradeLabel) + 14);
                lineXPos = Math.max(128, leftStart + 22 + codeLevelTagWidth + 10);
                this.setFont(doc, 'table_td', null, 'normal');
                doc.setFontSize(11);
                codeLevelGuideMsg = doc.splitTextToSize(guideMsgText, leftStart + this.pageContentWidth - (lineXPos + 16) - 8);
                codeLevelGuideMsgDim = doc.getTextDimensions(codeLevelGuideMsg);
                codeResultCardHeight = Math.max(62, codeLevelGuideMsgDim.h + 24);
            }

            position_y = this.addTitle(doc, position_y + 2, 2, this.$t('코드별 평가', this.report_lang), {skip_divider: true});

            const codeResultCardYPos = position_y - 10;
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.rect(leftStart, codeResultCardYPos, this.pageContentWidth, codeResultCardHeight, 'FD');
            
            const complianceLevelTextYPos = codeResultCardYPos + 18;
            doc.text(this.$t('준수 수준', this.report_lang), leftStart + 12, complianceLevelTextYPos, {align: 'left'});
            
            doc.setDrawColor('#c2bfbf');
            doc.setLineWidth(1);
            doc.setFillColor(...(this.report_colors.state[cate3.category3_ev_code ?? 'N'] ?? this.report_colors.state.N));
            const codeLevelTagXPos = leftStart + 22;
            const codeLevelTagYPos = complianceLevelTextYPos + 8;
            doc.roundedRect(codeLevelTagXPos, codeLevelTagYPos, codeLevelTagWidth, 24, 4, 4, 'FD');

            this.setFont(doc, 'table_td', null, 'bold');
            doc.setTextColor('#ffffff');
            if (cate3.category3_ev_code === 'B' || cate3.category3_ev_code === 'C') {
                doc.setTextColor('#232323');
            }
            doc.setFontSize(isEn ? 12 : 16);
            const codeLevelDescText = doc.splitTextToSize(gradeLabel, this.pageContentWidth);
            const codeLevelDescTextDim = doc.getTextDimensions(codeLevelDescText);
            const codeLevelDescTextXPos = codeLevelTagXPos + (codeLevelTagWidth - codeLevelDescTextDim.w) / 2;
            doc.text(codeLevelDescText, codeLevelDescTextXPos, codeLevelTagYPos + (isEn ? 16 : 17), {align: 'left'});

            doc.setLineWidth(0.75);
            doc.setDrawColor('#E0E0E0');
            const lineYPosBegin = codeResultCardYPos + 8;
            doc.line(lineXPos, lineYPosBegin, lineXPos, lineYPosBegin + codeResultCardHeight - 16);

            if (!isEn) {
                // 국문: 기존 그대로 (이 시점의 글꼴 상태로 줄을 나눈다)
                codeLevelGuideMsg = doc.splitTextToSize(guideMsgText, this.pageContentWidth - 14);
                codeLevelGuideMsgDim = doc.getTextDimensions(codeLevelGuideMsg);
            }
            const codeLevelGuideMsgYPos = lineYPosBegin + (codeResultCardHeight - 16 - codeLevelGuideMsgDim.h) / 2 + 4;
            
            this.setFont(doc, 'table_td', null, 'normal');
            doc.setTextColor('#232323');
            doc.setFontSize(11);
            doc.text(codeLevelGuideMsg, lineXPos + 16, codeLevelGuideMsgYPos + 10, {align: 'left'});
            
            position_y += codeResultCardHeight;
            
            return position_y;
        },
        async codeResult_scoreCard(doc, position_y, cate3) {
            const code = cate3.category3_question_name.split('.')[0];
            const levelDesc = this.codeLevelDescOf(code);
            
            const cellWidth = this.pageContentWidth / 4;
            const codeStyle = {
                ...this.tableStyles.th,
                lineColor: '#232323',
                cellWidth,
                halign: 'center',
                fontStyle: 'bold',
                lineWidth: 1
            };
            let tableBody = [];
            
            if (cate3.category3_ev_code === 'N') {
                tableBody = [
                    [
                        {
                            content: this.$t('매우 미흡', this.report_lang),
                            styles: {...codeStyle, fillColor: this.report_colors.muted, textColor: '#ffffff'}
                        },
                        {
                            content: this.$t('미흡 ', this.report_lang),
                            styles: {...codeStyle, fillColor: this.report_colors.muted, textColor: '#ffffff'}
                        },
                        {
                            content: this.$t('보통', this.report_lang),
                            styles: {...codeStyle, fillColor: this.report_colors.muted, textColor: '#ffffff'}
                        },
                        {
                            content: this.$t('우수', this.report_lang),
                            styles: {...codeStyle, fillColor: this.report_colors.muted, textColor: '#ffffff'}
                        },
                    ],
                    [
                        {
                            content: levelDesc.D,
                            styles: {...codeStyle, fontStyle: 'medium', fontSize: 10, halign: 'left', fillColor: '#bebebe'}
                        },
                        {
                            content: levelDesc.C,
                            styles: {...codeStyle, fontStyle: 'medium', fontSize: 10, halign: 'left', fillColor: '#bebebe'}
                        },
                        {
                            content: levelDesc.B,
                            styles: {...codeStyle, fontStyle: 'medium', fontSize: 10, halign: 'left', fillColor: '#bebebe'}
                        },
                        {
                            content: levelDesc.A,
                            styles: {...codeStyle, fontStyle: 'medium', fontSize: 10, halign: 'left', fillColor: '#bebebe'}
                        },
                    ],
                ];
                
                if (levelDesc.A === '-') {
                    tableBody[1][3].styles.halign = 'center';
                }
            } 
            else {
                tableBody = [
                    [
                        {
                            content: this.$t('매우 미흡', this.report_lang),
                            styles: {...codeStyle, fillColor: this.report_colors.state.D, textColor: '#ffffff'}
                        },
                        {
                            content: this.$t('미흡 ', this.report_lang),
                            styles: {...codeStyle, fillColor: this.report_colors.state.C}
                        },
                        {
                            content: this.$t('보통', this.report_lang),
                            styles: {...codeStyle, fillColor: this.report_colors.state.B}
                        },
                        {
                            content: this.$t('우수', this.report_lang),
                            styles: {...codeStyle, fillColor: this.report_colors.state.A, textColor: '#ffffff'}
                        },
                    ],
                    [
                        {
                            content: levelDesc.D,
                            styles: {
                                ...codeStyle,
                                fontStyle: 'medium',
                                halign: 'left',
                                fontSize: 10,
                                fillColor: rgbaToRgb([...this.report_colors.state.D, 0.5])
                            }
                        },
                        {
                            content: levelDesc.C,
                            styles: {
                                ...codeStyle,
                                fontStyle: 'medium',
                                halign: 'left',
                                fontSize: 10,
                                fillColor: rgbaToRgb([...this.report_colors.state.C, 0.5])
                            }
                        },
                        {
                            content: levelDesc.B,
                            styles: {
                                ...codeStyle,
                                fontStyle: 'medium',
                                halign: 'left',
                                fontSize: 10,
                                fillColor: rgbaToRgb([...this.report_colors.state.B, 0.5])
                            }
                        },
                        {
                            content: levelDesc.A,
                            styles: {
                                ...codeStyle,
                                fontStyle: 'medium',
                                halign: 'left',
                                fontSize: 10,
                                fillColor: rgbaToRgb([...this.report_colors.state.A, 0.5])
                            }
                        },
                    ],
                ];
                
                if (levelDesc.A === '-') {
                    tableBody[0][3].styles.fillColor = this.report_colors.muted;
                    tableBody[1][3].styles.fillColor = '#bebebe';
                    tableBody[1][3].styles.halign = 'center';
                }
            }
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y + 4,
                margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing - 5},
                tableWidth: this.pageContentWidth,
                pageBreak: 'auto',
                rowPageBreak: 'avoid',
                styles: {
                    ...this.tableStyles.global,
                    lineWidth: 0
                },
                body: tableBody,
                didDrawPage: (hookData) => {
                    if(cate3.category3_ev_code === 'N') return;
                    
                    const firstCellX = this.jsPDFOpt.margin.left;
                    const cellWidth = this.pageContentWidth / 4;
                    const gradeToColIndex = { D: 0, C: 1, B: 2, A: 3 };
                    
                    const targetColIndex = gradeToColIndex[cate3.category3_ev_code];
                    const targetBoxX = firstCellX + (cellWidth * targetColIndex);
                    let tableHeight = 0;
                    hookData.table.body.forEach(row => {
                        tableHeight += row.height;
                    });
                    // 현재 준수 수준에 박스
                    doc.setDrawColor(this.report_colors.selected);
                    doc.setLineWidth(3);
                    doc.rect(targetBoxX - 0.5, hookData.settings.startY,  cellWidth + 0.5, tableHeight, 'S');
                    
                }
            });
            return doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
        },
        async codeResult_nonComplianceDetail(doc, position_y, cate3) {
            const titleMsg = doc.splitTextToSize(this.$t('문항별 주요 개선 필요 항목', this.report_lang), this.pageContentWidth);
            const titleDim = doc.getTextDimensions(titleMsg);
            position_y = this.addTitle(doc, position_y, 2, titleMsg, {skip_divider: true});
            
            const leftStart = this.jsPDFOpt.margin.left;
            let lineXPos = leftStart + titleDim.w + 24;
            const lineYPos = position_y - 18;
            if(this.report_lang === 'ko'){
                doc.setLineWidth(1);
                doc.setDrawColor('#343a40');
                doc.line(lineXPos, lineYPos, this.pageContentWidth + leftStart, lineYPos);
            }
            
            
            const code = cate3.category3_question_name.split('.')[0];
            const targetCodeEvData =  this.evBaseData.processedEvData[code] || {};

            const codeEvDataObjList = Object.entries(targetCodeEvData).filter(([subCode]) => {
                const targetSub = this.subBaseData.mapSubCodeData[subCode];
                return targetSub.ev_code === 'C' || targetSub.ev_code === 'D';
            });
            
            if(codeEvDataObjList.length < 1){
                const emptyCardYPos = position_y - 4;
                doc.setDrawColor('#c2bfbf');
                doc.setLineWidth(0.6);
                doc.setFillColor(...rgbaToRgb([...this.report_colors.state.N, 0.25]));
                doc.rect(leftStart, emptyCardYPos, this.pageContentWidth, 80, 'FD');
                this.setFont(doc, 'table_td', null, 'medium');
                doc.setFontSize(11);
                doc.setTextColor('#666666');
                doc.text(this.$t('코드 요구사항 준수하여, 주요 개선 필요 항목 없음', this.report_lang), leftStart + this.pageContentWidth / 2, emptyCardYPos + 40, {align: 'center'});

                return position_y;
            }
            else {
                this.setFont(doc, 'table_td', null, 'normal');
                doc.setTextColor('#666666');
                doc.setFontSize(11);
                const basicGuideMsg = doc.splitTextToSize(this.$t('nonComplianceGuideMsg' , this.report_lang, {code: code}), this.pageContentWidth);
                doc.text(basicGuideMsg, leftStart, position_y + 2, {align: 'left'});
                
                position_y += doc.getTextDimensions(basicGuideMsg).h + 10;
            }
            
            codeEvDataObjList.forEach(([subCode, evItems]) => {
                if (this.checkNeedNewPage(doc, position_y, 80)) {
                    this.addDocPage(doc, true);
                    position_y = this.firstElementSpacing;
                }
                const targetSub = this.subBaseData.mapSubCodeData[subCode];
                // subLabel
                this.setFont(doc, 'table_td', null, 'medium');
                doc.setTextColor('#232323');
                doc.setFontSize(11);
                const subLabelPosY = position_y + 4;
                doc.text(this.$t('문항 ', this.report_lang) + targetSub.label + ':', leftStart, subLabelPosY, {align: 'left'});
                
                position_y += 8;
                
                const thStyle = {
                    ...this.tableStyles.th,
                    lineColor: '#232323',
                    halign: 'center',
                    fontStyle: 'bold',
                };
                

                const tableBody = [
                    [
                        {
                            content: this.$t('평가 결과', this.report_lang),
                            colSpan: 3,
                            styles: {...thStyle }
                        },
                        {
                            content: this.$t('개선 권고사항', this.report_lang),
                            styles: {...thStyle }
                        },
                    ]
                ];
                
                Object.entries(evItems).forEach(([ev_id, evItem]) => {
                    if(ev_id === 'isNA') return;
                    const ev_code = evItem.ev_code;

                    if(ev_code === 'A' || ev_code === 'B' || ev_code === 'N' || ev_code == null) return;


                    const tdStyle = {
                        ...this.tableStyles.td,
                        lineColor: '#232323',
                        halign: 'center',
                        fontStyle: 'normal',
                    };


                    // 개선표 문구가 등록되지 않은 평가 요소는 빈 칸으로 둔다 (undefined.asIs 로 throw 하면 이후 렌더가 통째로 잘린다)
                    // content 가 undefined/null 이면 jspdf-autotable 이 셀 정의 객체를 문자열로 바꿔 "[object Object]" 를 찍는다 → 반드시 '' 로.
                    const asIsToBe = codeResult.evAsIsToBeMsg[ev_id] || {};

                    tableBody.push([
                        {
                            content: '',
                            ev_code,
                            styles: {...tdStyle },
                        },
                        {
                            content: localized(this.report_lang, asIsToBe.asIs, 'asIsToBe', ev_id, 'asIs') || '',
                            styles: {...tdStyle, halign: 'left', },
                        },
                        {
                            content: '',
                            data: {
                                code,
                                subCode,
                                ev_id,
                            },
                            styles: {...tdStyle },
                        },
                        {
                            content: localized(this.report_lang, asIsToBe.toBe, 'asIsToBe', ev_id, 'toBe') || '',
                            styles: {...tdStyle, halign: 'left', },
                        },
                    ]);

                });

                const leftCellWidth = this.pageContentWidth * 0.57;
                let auditBasisWidth = 16;
                // 등급 태그: 국문은 기존 폭(태그 32 / 칸 40). 영문 'Critical Laggard' 는 글자 폭에 맞춘다.
                let levelTagWidth = 32;
                if (this.report_lang === 'en') {
                    this.setFont(doc, 'table_td', null, 'bold');
                    doc.setFontSize(8.5);
                    levelTagWidth = Math.max(32, doc.getTextWidth(this.$t('매우 미흡', this.report_lang)) + 10);
                }
                const levelColWidth = levelTagWidth + 8;
                autoTable(doc, {
                    theme: 'grid',
                    startY: position_y,
                    margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing - 5},
                    tableWidth: this.pageContentWidth,
                    columnStyles: {
                        0: { cellWidth: levelColWidth },
                        1: { cellWidth: leftCellWidth - levelColWidth - auditBasisWidth },
                        2: { cellWidth: auditBasisWidth },
                        3: { cellWidth: this.pageContentWidth - leftCellWidth }
                    },
                    pageBreak: 'auto',
                    rowPageBreak: 'avoid',
                    body: tableBody,
                    willDrawCell: (data) => {
                        if (data.section === 'body') {
                            const {cell} = data;
                            
                            if (cell.raw?.ev_code != null) {
                                cell.text = [];
                            }
                        }
                    },
                    didDrawCell: (data) => {
                        if (data.column.index === 0 && data.row.index > 0) {
                            const doc = data.doc;
                            const ev_code = data.cell.raw.ev_code;
                            // 색·등급 설명이 없는 코드는 태그를 건너뛴다 (훅에서 throw 하면 이후 렌더가 통째로 빠짐)
                            const tagColor = this.report_colors.state[ev_code];
                            const levelDescItem = this.reportData.ev_code_list.find(item => item.code === ev_code);
                            if (!tagColor || !levelDescItem) return;

                            // 태그 위치 계산
                            const tagWidth = levelTagWidth;
                            const tagHeight = 11;
                            const tagX = data.cell.x + (data.cell.width - tagWidth) / 2;
                            const tagY = data.cell.y + (data.cell.height - tagHeight) / 2;
                            
                            // 태그 배경
                            doc.setDrawColor('#c2bfbf');
                            doc.setLineWidth(1);
                            doc.setFillColor(...tagColor);
                            doc.roundedRect(tagX, tagY, tagWidth, tagHeight, 4, 4, 'FD');

                            // 태그 텍스트
                            this.setFont(doc, 'table_td', null, 'bold');
                            doc.setFontSize(8.5);
                            doc.setTextColor('#ffffff');
                            if (ev_code === 'B' || ev_code === 'C') {
                                doc.setTextColor('#232323');
                            }

                            const targetLevelDesc = levelDescItem.description;
                            const levelText = this.$t(targetLevelDesc, this.report_lang);
                            const textDim = doc.getTextDimensions(levelText);
                            const textX = tagX + (tagWidth - textDim.w) / 2;
                            const textY = tagY + tagHeight / 2 + textDim.h / 2 - 1;
                            doc.text(levelText, textX, textY);
                        }
                        if (data.column.index === 2 && data.row.index > 0) {
                            const {cell} = data;
                            const doc = data.doc;
                            const iconSize = 8;
                            const iconX = data.cell.x + (data.cell.width - iconSize) / 2;
                            const iconY = data.cell.y + (data.cell.height - iconSize) / 2;
                            
                            doc.addImage(ShortcutIcon, 'SVG', iconX, iconY, iconSize, iconSize);
                            const {ev_id} = cell.raw.data;
                            
                            this.linkDestinationInfo.toAppendix[ev_id] = {
                                linkSource: {
                                    pageNumber: doc.internal.getCurrentPageInfo().pageNumber,
                                    x: cell.x,
                                    y: cell.y,
                                    width: cell.width,
                                    height: cell.height
                                },
                                linkDestination: {}
                            };
                            let positionY = cell.y - 60;
                            if(cell.y - 60 < this.jsPDFOpt.margin.top){
                                positionY = this.jsPDFOpt.margin.top;
                            }
                            this.linkDestinationInfo.toImprovement[ev_id] = {
                                linkSource: {},
                                linkDestination: {
                                    pageNumber: doc.internal.getCurrentPageInfo().pageNumber,
                                    y: positionY
                                }
                            };
                        }
                    },
                    didDrawPage: (HookData) => {
                        this.addHeader(HookData.doc);
                    },
                });
                position_y = doc.lastAutoTable.finalY + 6;
                
                let baseXPos = leftStart + this.pageContentWidth;
                const iconSize = 8;
                const iconY = position_y - 4;
                this.setFont(doc, 'table_td', null, 'normal');
                doc.setTextColor('#666666');
                doc.setFontSize(8.5);
                const iconDescText = `: ${this.$t('클릭 시, Appendix의 해당 페이지에서 개선 필요 항목으로 평가된 상세 근거를 확인하실 수 있습니다.', this.report_lang)}`;
                position_y += this.drawIconDesc(doc, iconDescText, baseXPos, position_y, iconSize, iconY);

                position_y += 12 + this.jsPDFOpt.spacing.section_xs;
            });
            return position_y;
        },
        async codeSummary_AreaCompliance(doc, position_y) {
            const [humanRight, environment, governance] = this.levelData.category1;
            
            let chartCount = 0;
            [humanRight, environment, governance].forEach((item) => {
                if(item != null)
                    chartCount += 1;
            });
            
            if(chartCount === 1) // 사내 도급의 경우 Area 영역 필요 없음
                return position_y;
            
            position_y = this.addTitle(doc, position_y, 1, this.$t('영역별 준수율', this.report_lang));
            
            const chartWidth = 100;
            let chartPadding = (this.pageContentWidth - chartWidth * chartCount) / 2;
            
            if(chartCount === 2){
                chartPadding = (this.pageContentWidth - chartWidth * chartCount) / 3;
            }
            
            let chartXPos = this.jsPDFOpt.margin.left;
            let labelPosY = position_y + 12;
            
            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setTextColor(29, 29, 29);
            doc.setFontSize(14);
            
            
            // 인권 차트
            if(chartCount < 3){
                chartXPos += chartPadding;
            }
            const humanRightText = doc.splitTextToSize(this.$t('인권', this.report_lang), this.pageContentWidth);
            const humanRightTextDim = doc.getTextDimensions(humanRightText);
            const humanRightTextXPos = chartXPos + (chartWidth - humanRightTextDim.w) / 2;
            doc.text(humanRightText, humanRightTextXPos, labelPosY);
            
            const compliance = humanRight.category1_compliance_rate === 'N/A' ? 0 : humanRight.category1_compliance;
            const nonCompliance = humanRight.category1_compliance_rate === 'N/A' ? 100 : humanRight.category1_noncompliance;
            const nonComplianceColor = humanRight.category1_compliance_rate === 'N/A' ? this.report_colors.muted : this.report_colors.nonCompliance;
            
            const humanRightChart = await this.generateCodeSummaryPieChartInstance(
                [compliance, nonCompliance],
                {data1: this.report_colors.compliance, data2: nonComplianceColor},
            );
            await waitTime(100);
            doc.addImage(humanRightChart, 'PNG', chartXPos, labelPosY + 12, chartWidth, chartWidth, undefined, 'FAST');
            this.codeDoughnutChartInstance?.destroy();
            this.codeDoughnutChartInstance = null;
            
            doc.setFontSize(24);
            const complianceRate = humanRight.category1_compliance_rate === 'N/A' ? 'N/A' :  this.roundToOneDecimalPlace(humanRight.category1_compliance_rate) + '%';
            const humanRightPercent = doc.splitTextToSize(complianceRate, this.pageContentWidth);
            const humanRightPercentDim = doc.getTextDimensions(humanRightPercent);
            const humanRightPercentXPos = chartXPos + (chartWidth - humanRightPercentDim.w) / 2;
            const humanRightPercentYPos = labelPosY + 12 + (chartWidth + humanRightPercentDim.h) / 2;
            doc.text(humanRightPercent, humanRightPercentXPos, humanRightPercentYPos);
            
            chartXPos += chartWidth + chartPadding;
            
            if(chartCount > 1){
                // 환경 차트
                doc.setFontSize(14);
                const environmentText = doc.splitTextToSize(this.$t('환경', this.report_lang), this.pageContentWidth);
                const environmentTextDim = doc.getTextDimensions(environmentText);
                const environmentTextXPos = chartXPos + (chartWidth - environmentTextDim.w) / 2;
                doc.text(environmentText, environmentTextXPos, labelPosY);
                
                const compliance = environment.category1_compliance_rate === 'N/A' ? 0 : environment.category1_compliance;
                const nonCompliance = environment.category1_compliance_rate === 'N/A' ? 100 : environment.category1_noncompliance;
                const nonComplianceColor = environment.category1_compliance_rate === 'N/A' ? this.report_colors.muted : this.report_colors.nonCompliance;
                
                const environmentChart = await this.generateCodeSummaryPieChartInstance(
                    [compliance, nonCompliance],
                    {data1: this.report_colors.compliance, data2: nonComplianceColor},
                );
                await waitTime(100);
                doc.addImage(environmentChart, 'PNG', chartXPos, labelPosY + 12, chartWidth, chartWidth, undefined, 'FAST');
                this.codeDoughnutChartInstance?.destroy();
                this.codeDoughnutChartInstance = null;
                
                doc.setFontSize(24);
                const complianceRate = environment.category1_compliance_rate === 'N/A' ? 'N/A' :  this.roundToOneDecimalPlace(environment.category1_compliance_rate) + '%';
                const environmentPercent = doc.splitTextToSize(complianceRate, this.pageContentWidth);
                const environmentPercentDim = doc.getTextDimensions(environmentPercent);
                const environmentPercentXPos = chartXPos + (chartWidth - environmentPercentDim.w) / 2;
                const environmentPercentYPos = labelPosY + 12 + (chartWidth + environmentPercentDim.h) / 2;
                doc.text(environmentPercent, environmentPercentXPos, environmentPercentYPos);
                
                chartXPos += chartWidth + chartPadding;
            }
            
            if(chartCount > 2){
                // 거버넌스 차트
                doc.setFontSize(14);
                const governanceText = doc.splitTextToSize(this.$t('거버넌스', this.report_lang), this.pageContentWidth);
                const governanceTextDim = doc.getTextDimensions(governanceText);
                const governanceTextXPos = chartXPos + (chartWidth - governanceTextDim.w) / 2;
                doc.text(governanceText, governanceTextXPos, labelPosY);
                
                const compliance = governance.category1_compliance_rate === 'N/A' ? 0 : governance.category1_compliance;
                const nonCompliance = governance.category1_compliance_rate === 'N/A' ? 100 : governance.category1_noncompliance;
                const nonComplianceColor = governance.category1_compliance_rate === 'N/A' ? this.report_colors.muted : this.report_colors.nonCompliance;
                
                const governanceChart = await this.generateCodeSummaryPieChartInstance(
                    [compliance, nonCompliance],
                    {data1: this.report_colors.compliance, data2: nonComplianceColor},
                );
                await waitTime(100);
                doc.addImage(governanceChart, 'PNG', chartXPos, labelPosY + 12, chartWidth, chartWidth, undefined, 'FAST');
                this.codeDoughnutChartInstance?.destroy();
                this.codeDoughnutChartInstance = null;
                
                doc.setFontSize(24);
                const complianceRate = governance.category1_compliance_rate === 'N/A' ? 'N/A' :  this.roundToOneDecimalPlace(governance.category1_compliance_rate) + '%';
                const governancePercent = doc.splitTextToSize(complianceRate, this.pageContentWidth);
                const governancePercentDim = doc.getTextDimensions(governancePercent);
                const governancePercentXPos = chartXPos + (chartWidth - governancePercentDim.w) / 2;
                const governancePercentYPos = labelPosY + 12 + (chartWidth + governancePercentDim.h) / 2;
                doc.text(governancePercent, governancePercentXPos, governancePercentYPos);
            }
           
            
            
            
            position_y += (chartWidth + this.jsPDFOpt.spacing.section_xs + 40);
            
            return position_y;
        },
        async codeSummary_CodeCompliance(doc, position_y) {
            position_y = this.addTitle(doc, position_y, 1, this.$t('Code별 준수결과', this.report_lang));
            
            const category1Data = JSON.parse(JSON.stringify(this.reportData.results[2].data));
            const category2Data = JSON.parse(JSON.stringify(this.reportData.results[3].data));
            const category3Data = JSON.parse(JSON.stringify(this.reportData.results[4].data));
            const thStyle = {...this.tableStyles.th, lineColor: '#232323', fontSize: 11 };
            // 등급 헤더 칸: 국문은 기존(폭 25, 11pt). 영문은 'Critical' 이 25 에 안 들어가 단어 중간에서 끊기므로 칸을 넓히고 글자를 줄인다.
            const isEn = this.report_lang === 'en';
            const gradeThStyle = {...thStyle, cellWidth: isEn ? 40 : 25, halign: 'center', ...(isEn ? {fontSize: 8} : {})};

            const tableBody = [
                [
                    {
                        content: this.$t('대분류', this.report_lang),
                        styles: {...thStyle, cellWidth: 50, halign: 'center'}
                    },
                    {
                        content: this.$t('중분류', this.report_lang),
                        styles: {...thStyle, cellWidth: 50, halign: 'center'}
                    },
                    {
                        content: this.$t('Code', this.report_lang),
                        styles: {...thStyle, halign: 'center'}
                    },
                    {
                        content: this.$t('매우미흡', this.report_lang),
                        styles: {...gradeThStyle}
                    },
                    {
                        content: this.$t('미흡', this.report_lang),
                        styles: {...gradeThStyle}
                    },
                    {
                        content: this.$t('보통', this.report_lang),
                        styles: {...gradeThStyle}
                    },
                    {
                        content: this.$t('우수', this.report_lang),
                        styles: {...gradeThStyle}
                    },
                ]
            ];
            
            const scoreCnt = {
                'A': 0,
                'B': 0,
                'C': 0,
                'D': 0,
            };
            let currentTarget = {
                cat1: null,
                cat2: null,
            };
            category3Data.forEach((category3, cate3Idx) => {
                tableBody.push([]);
                let cateIdx = tableBody.length - 1;
                
                const code = category3.category3_question_name.split('.')[0];
                
                const targetCat1 = category1Data.find(cat1 => {
                    const currentCat1Code = code.split('-')[0];
                    return cat1.category1_question_name.split('.')[0] === currentCat1Code;
                });
                if (currentTarget.cat1?.category1_question_code !== targetCat1.category1_question_code) {
                    currentTarget.cat1 = targetCat1;
                    
                    const rowSpan = category3Data.filter(cat3 => {
                        return cat3.category3_question_name.includes(targetCat1.category1_question_name.split('.')[0]);
                    }).length;
                    
                    tableBody[cateIdx].push(
                        {
                            content: this.questionNameOf(targetCat1.category1_question_name),
                            rowSpan,
                            styles: {
                                ...this.tableStyles.td,
                                fontSize: 10.5,
                                fontStyle: 'bold',
                                textColor: '#292828',
                                halign: 'center'
                            }
                        },
                    );
                }
                
                const targetCat2 = category2Data.find(cat2 => {
                    const codeSplit = code.split('-');
                    const currentCat2Code = `${codeSplit[0]}-${codeSplit[1]}`;
                    return cat2.category2_question_name.split('.')[0] === currentCat2Code;
                });
                
                
                if (currentTarget.cat2?.category2_question_code !== targetCat2.category2_question_code) {
                    currentTarget.cat2 = targetCat2;
                    
                    const rowSpan = category3Data.filter(cat3 => {
                        return cat3.category3_question_name.includes(targetCat2.category2_question_name.split('.')[0]);
                    }).length;
                    
                    tableBody[cateIdx].push({
                        content: this.questionNameOf(targetCat2.category2_question_name),
                        rowSpan,
                        styles: {
                            ...this.tableStyles.td,
                            fontSize: 10,
                            fontStyle: 'semibold',
                            halign: 'left',
                            textColor: '#292828'
                        },
                        
                    });
                }
                
                tableBody[cateIdx].push({
                    content: `${code}. ${this.codeConductOf(code, category3.category3_question_name)}`, styles: {...this.tableStyles.td, fontSize: 9,minCellHeight: 20,   halign: 'left'},
                });
                
                if (category3.category3_ev_code === 'N' || category3.category3_ev_code == null) {
                    tableBody[cateIdx].push({
                        content: '', styles: {...this.tableStyles.td, fillColor: '#bebebe', halign: 'center'},
                    });
                    tableBody[cateIdx].push({
                        content: '', styles: {...this.tableStyles.td, fillColor: '#bebebe', halign: 'center'},
                    });
                    tableBody[cateIdx].push({
                        content: '', styles: {...this.tableStyles.td, fillColor: '#bebebe', halign: 'center'},
                    });
                    tableBody[cateIdx].push({
                        content: '', styles: {...this.tableStyles.td, fillColor: '#bebebe', halign: 'center'},
                    });
                } else {
                    tableBody[cateIdx].push({
                        content: '',
                        styles: {
                            ...this.tableStyles.td,
                            fillColor: category3.category3_ev_code === 'D' ? this.report_colors.state.D : '#ffffff'
                        },
                    });
                    tableBody[cateIdx].push({
                        content: '',
                        styles: {
                            ...this.tableStyles.td,
                            fillColor: category3.category3_ev_code === 'C' ? this.report_colors.state.C : '#ffffff'
                        },
                    });
                    tableBody[cateIdx].push({
                        content: '',
                        styles: {
                            ...this.tableStyles.td,
                            fillColor: category3.category3_ev_code === 'B' ? this.report_colors.state.B : '#ffffff'
                        },
                    });
                    tableBody[cateIdx].push({
                        content: '',
                        styles: {
                            ...this.tableStyles.td,
                            fillColor: category3.category3_ev_code === 'A' ? this.report_colors.state.A : '#ffffff'
                        },
                    });
                }
                
                scoreCnt[category3.category3_ev_code] += 1;
            });
            
            tableBody.push(
                [
                    {
                        content: 'TOTAL',
                        colSpan: 3,
                        styles: {
                            ...this.tableStyles.td,
                            fontSize: 11,
                            fontStyle: 'bold',
                            halign: 'center',
                            fillColor: '#666666',
                            textColor: '#ffffff'
                        }
                    },
                    {
                        content: scoreCnt.D.toString(),
                        styles: {
                            ...this.tableStyles.td,
                            fontSize: 11,
                            fontStyle: 'bold',
                            halign: 'center',
                            fillColor: '#666666',
                            textColor: '#ffffff'
                        }
                    },
                    {
                        content: scoreCnt.C.toString(),
                        styles: {
                            ...this.tableStyles.td,
                            fontSize: 11,
                            fontStyle: 'bold',
                            halign: 'center',
                            fillColor: '#666666',
                            textColor: '#ffffff'
                        }
                    },
                    {
                        content: scoreCnt.B.toString(),
                        styles: {
                            ...this.tableStyles.td,
                            fontSize: 11,
                            fontStyle: 'bold',
                            halign: 'center',
                            fillColor: '#666666',
                            textColor: '#ffffff'
                        }
                    },
                    {
                        content: scoreCnt.A.toString(),
                        styles: {
                            ...this.tableStyles.td,
                            fontSize: 11,
                            fontStyle: 'bold',
                            halign: 'center',
                            fillColor: '#666666',
                            textColor: '#ffffff'
                        }
                    },
                ]
            );
            
            this.setFont(doc, 'table_td', null);
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(9);
            const naGuideMsg = doc.splitTextToSize(this.$t('해당 없음', this.report_lang), this.pageContentWidth);
            const naGuideMsgDim = doc.getTextDimensions(naGuideMsg);
            const naGuideMsgPosX = this.pageContentWidth + this.jsPDFOpt.margin.left - naGuideMsgDim.w;
            doc.text(naGuideMsg, naGuideMsgPosX, position_y + 2);
            
            doc.setLineWidth(0);
            doc.setFillColor('#bebebe');
            doc.rect(naGuideMsgPosX - 20, position_y - 4.5, 16, 8, 'FD');
            
            const legendLabel = this.$t('범례', this.report_lang) + ':';
            // 국문은 기존 위치 그대로. 영문 라벨은 더 길어 회색 상자와 겹치므로 글자 폭만큼 왼쪽에서 시작
            doc.text(legendLabel, isEn ? naGuideMsgPosX - 24 - doc.getTextWidth(legendLabel) : naGuideMsgPosX - 38, position_y + 2);
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y + 10,
                margin: {...this.jsPDFOpt.margin, top: this.firstElementSpacing - 5},
                tableWidth: this.pageContentWidth,
                pageBreak: 'auto',
                rowPageBreak: 'avoid',
                styles: {...this.tableStyles.global, lineWidth: 0.7, lineColor: '#232323'},
                body: tableBody,
                didDrawPage: (HookData) => {
                    this.addHeader(HookData.doc);
                }
            });
            
            return doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
        },
        
        summary_processCompanyInfo(doc, position_y) {
            // 기업정보 섹션
            position_y = this.addTitle(doc, position_y, 2, '기업 정보', {
                skip_divider: true,
                no_padding: true,
                no_margin: true
            });
            let _column_width = this.pageContentWidth / 4;
            
            const parseWorker = (worker_num) => {
                if (worker_num === ' - ') return 0;
                return Number(worker_num.toString().replaceAll(',', ''));
            };
            /**
             * @todo 근로자 계산식 통일 필요.
             */
            const worker_num = parseWorker(this.companyInfo['근로자수']);
            const companySize = this.companyInfo['기업규모'];
            // 영문은 숫자와 단위 사이를 띄운다 ("120 employees")
            const workerUnit = `${this.report_lang === 'en' ? ' ' : ''}${this.$t('명', this.report_lang)}`;

            const companyInfoBody = [
                [
                    {
                        content: this.$t('기업명', this.report_lang),
                        styles: {...this.tableStyles.th, cellWidth: _column_width}
                    },
                    {content: this.companyInfo['회사명'], styles: {...this.tableStyles.td, cellWidth: _column_width * 3}},
                ],
                [
                    {
                        content: this.$t('기업규모', this.report_lang),
                        styles: {...this.tableStyles.th, cellWidth: _column_width}
                    },
                    {
                        // 규모 라벨(대기업/중기업/소기업/사내도급)은 보고서 언어로
                        content: typeof companySize === 'string' ? this.$t(companySize, this.report_lang) : companySize,
                        styles: {...this.tableStyles.td, cellWidth: _column_width * 3}
                    },
                ],
                [
                    {
                        content: this.$t('근로자수', this.report_lang),
                        styles: {...this.tableStyles.th, cellWidth: _column_width}
                    },
                    {
                        content: `${this.formatNumber(worker_num.toString())}${workerUnit} (${this.$t('정규직', this.report_lang)}: ${this.formatNumber(this.companyInfo['근로자_정규직'])}, ${this.formatNumber(this.$t('계약직', this.report_lang))}: ${this.companyInfo['근로자_계약직']}, ${this.formatNumber(this.$t('일용직', this.report_lang))}: ${this.companyInfo['근로자_일용직']})`,
                        styles: {...this.tableStyles.td, cellWidth: _column_width * 3}
                    }
                ],
            ];
            // 자가진단 프로젝트는 규모 구분이 없는 템플릿이라 기업규모 행을 숨긴다 (report-self-assessment.cjs)
            if (isSelfAssessmentProject(this.reportData?.info?.project_code)) {
                companyInfoBody.splice(1, 1);
            }

            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: this.jsPDFOpt.margin,
                // styles: this.jsPDFOpt.tableOpt.common,
                styles: this.tableStyles.global,
                body: companyInfoBody,
                didDrawCell: (data) => this.jspdf_common_cell_font_convert(data)
            });
            
            
            position_y = doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
            
            // 평가정보 섹션
            position_y = this.addTitle(doc, position_y, 2, this.$t('평가 정보', this.report_lang), {
                skip_divider: true,
                no_padding: true,
                no_margin: true
            });
            let _column_width_1 = _column_width;
            let _column_width_2 = this.pageContentWidth - _column_width_1;
            
            const getFormattedDate = (date) => {
                if (!date) return ' - ';
                return this.report_lang === 'ko'
                    ? dayjs(date).format('YYYY년 MM월 DD일')
                    : dayjs(date).format('MM/DD/YYYY');
            };
            
            let _assessment_info_table_body = [
                [
                    {
                        content: this.$t('평가기간', this.report_lang),
                        styles: {...this.tableStyles.th, cellWidth: _column_width_1}
                    },
                    {
                        content: `${this.assessmentInfo.requested_date} ~ ${this.assessmentInfo.duedate_date}`,
                        styles: {...this.tableStyles.td, cellWidth: _column_width_2}
                    }
                ],
            ];
            if ((this.assessmentType && this.assessmentType === 'OSIS') || (!this.assessmentType && this.reportData.info.service_type === '심화')) {
                _assessment_info_table_body.push(
                    [
                        {
                            content: this.$t('평가유형', this.report_lang),
                            styles: {...this.tableStyles.th, cellWidth: _column_width_1}
                        },
                        {
                            content: this.$t('실사평가', this.report_lang),
                            styles: {...this.tableStyles.td, cellWidth: _column_width_2}
                        }
                    ]
                );
            }
            
            autoTable(doc, {
                theme: 'grid',
                startY: position_y,
                margin: this.jsPDFOpt.margin,
                styles: this.tableStyles.global,
                body: _assessment_info_table_body
            });
            
            return doc.lastAutoTable.finalY + this.jsPDFOpt.spacing.section_xs;
        },
        async summary_processCodeCompliance(doc, position_y) {
            const chartXPos = this.jsPDFOpt.margin.left + 8;
            const rightContentXPos = chartXPos + 100 + 16;
            const rightContentWidth = (this.pageContentWidth - rightContentXPos + 20).toFixed(1);
            const {total,processedTotalCodeData} = this.levelData; 
            
            position_y = this.addTitle(doc, position_y, 2, this.$t('ESG Code 준수율', this.report_lang));
            this.setFont(doc, 'table_td', null, 'bold');
            doc.setFontSize(11);
            const complianceMsg = doc.splitTextToSize(summaryMsg.complianceRateMsg(this.report_lang, this.companyInfo['회사명'], this.levelData), rightContentWidth);
            const complianceMsgDim = doc.getTextDimensions(complianceMsg);
            // 표 3행 + 각주 높이. 영문은 등급 헤더('Critical Laggard')가 두 줄이 될 수 있어 여유를 둔다.
            const complianceTableHeight = this.report_lang === 'en' ? 78 : 66;
            const complianceMsgPosY = position_y + 16;
            let complianceChartPosY = position_y + 8;

            // 카드 높이는 기본 116, 안내 문장이 길어 넘칠 때만 늘린다 (전에는 `complianceMsgDim > 100` 으로
            // 객체를 숫자와 비교해 항상 false 였다 — 국문 3줄까지는 116 에 들어가 수치가 그대로다).
            let codeCardHeight = 116;
            const neededCardHeight = 16 + complianceMsgDim.h + complianceTableHeight + 4;
            if (neededCardHeight > codeCardHeight) {
                codeCardHeight = neededCardHeight;
                complianceChartPosY = position_y + (codeCardHeight - 100) / 2;
            }
            
            doc.setFillColor(...this.jsPDFOpt.card.fillColor);
            doc.rect(this.jsPDFOpt.margin.left, position_y, this.pageContentWidth, codeCardHeight, 'F');
            
            doc.text(complianceMsg, rightContentXPos, complianceMsgPosY);
            
            
            const rightPositionY = complianceMsgPosY + complianceMsgDim.h;
            const cellStyle = {...this.tableStyles.th, lineColor: '#292929', lineWidth: 1};
            
            autoTable(doc, {
                theme: 'grid',
                startY: rightPositionY,
                tableWidth: rightContentWidth,
                margin: {...this.jsPDFOpt.margin, top: 0, left: rightContentXPos},
                styles: {...this.tableStyles.global, halign: 'center'},
                body: [
                    [
                        {
                            content: this.$t('미준수', this.report_lang),
                            styles: {...cellStyle, fontStyle:'bold',  fillColor: rgbaToRgb([...this.report_colors.nonCompliance_rgb, 0.5])},
                            colSpan: 2
                        },
                        {
                            content: this.$t('준수', this.report_lang),
                            styles: {...cellStyle, fontStyle:'bold', fillColor: rgbaToRgb([...this.report_colors.compliance_rgb, 0.5])},
                            colSpan: 2
                        },
                    ],
                    [
                        {
                            content: this.$t('매우 미흡', this.report_lang),
                            styles: {
                                ...cellStyle,
                                cellWidth: rightContentWidth * 0.25,
                                fillColor: rgbaToRgb([...this.report_colors.nonCompliance_rgb, 0.3])
                            }
                        },
                        {
                            content: this.$t('미흡', this.report_lang),
                            styles: {
                                ...cellStyle,
                                cellWidth: rightContentWidth * 0.25,
                                fillColor: rgbaToRgb([...this.report_colors.nonCompliance_rgb, 0.3])
                            }
                        },
                        {
                            content: this.$t('보통', this.report_lang),
                            styles: {
                                ...cellStyle,
                                cellWidth: rightContentWidth * 0.25,
                                fillColor: rgbaToRgb([...this.report_colors.compliance_rgb, 0.3])
                            }
                        },
                        {
                            content: this.$t('우수', this.report_lang),
                            styles: {
                                ...cellStyle,
                                cellWidth: rightContentWidth * 0.25,
                                fillColor: rgbaToRgb([...this.report_colors.compliance_rgb, 0.3])
                            }
                        },
                    ],
                    [
                        {
                            content: this.$t('개수', this.report_lang, {count: processedTotalCodeData.D}),
                            styles: {...cellStyle, fillColor: rgbaToRgb([...this.report_colors.nonCompliance_rgb, 0.2])},
                        },
                        {
                            content: this.$t('개수', this.report_lang, {count: processedTotalCodeData.C}),
                            styles: {...cellStyle, fillColor: rgbaToRgb([...this.report_colors.nonCompliance_rgb, 0.2])},
                        },
                        {
                            content: this.$t('개수', this.report_lang, {count: processedTotalCodeData.B}),
                            styles: {...cellStyle, fillColor: rgbaToRgb([...this.report_colors.compliance_rgb, 0.2])},
                        },
                        {
                            content: this.$t('개수', this.report_lang, {count: processedTotalCodeData.A}),
                            styles: {...cellStyle, fillColor: rgbaToRgb([...this.report_colors.compliance_rgb, 0.2])},
                        },
                    ]
                ]
            });
            
            this.setFont(doc, 'table_td', null);
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(9);
            const complianceTableHelpMsg = doc.splitTextToSize(this.$t('* 미준수: \'매우 미흡 \' 및 \'미흡\' Code', this.report_lang), this.pageContentWidth);
            const complianceTableHelpMsgDim = doc.getTextDimensions(complianceTableHelpMsg);
            const complianceTableHelpMsgPosX = this.pageContentWidth + this.jsPDFOpt.margin.left - complianceTableHelpMsgDim.w - 9;
            doc.text(complianceTableHelpMsg, complianceTableHelpMsgPosX, doc.lastAutoTable.finalY + 10);
            
            const complianceRate =  this.roundToOneDecimalPlace((processedTotalCodeData.compliance / processedTotalCodeData.total) * 100);
            const nonComplianceRate =  this.roundToOneDecimalPlace((processedTotalCodeData.nonCompliance / processedTotalCodeData.total) * 100);
            
            const codeComplianceRateChart = await this.generateCodeSummaryPieChartInstance(
                [complianceRate, nonComplianceRate],
                {data1: this.report_colors.compliance, data2: this.report_colors.nonCompliance},
            );
            await waitTime(100);
            doc.addImage(codeComplianceRateChart, 'PNG', chartXPos, complianceChartPosY, 100, 100, undefined, 'FAST');
            this.codeDoughnutChartInstance?.destroy();
            this.codeDoughnutChartInstance = null;
            
            this.setFont(doc, 'table_td', null, 'bold');
            doc.setTextColor(29, 29, 29);
            doc.setFontSize(24);
            const complianceRateText = doc.splitTextToSize(total.total_compliance + '%', this.pageContentWidth);
            const complianceRateTextDim = doc.getTextDimensions(complianceRateText);
            const complianceRateTextPosX = chartXPos + (100 - complianceRateTextDim.w) / 2;
            const complianceRateTextPosY = complianceChartPosY + 100 - (100 - complianceRateTextDim.h) / 2;
            doc.text(complianceRateText, complianceRateTextPosX, complianceRateTextPosY);
            
            
            position_y += codeCardHeight + this.jsPDFOpt.spacing.section_xs;
            
            return {chartXPos, rightContentXPos, position_y};
        },
        async summary_processUnknown(doc, position_y, chartXPos, rightContentXPos) {
            position_y = this.addTitle(doc, position_y, 2, this.$t('평가 신뢰도', this.report_lang));
            
            const {unknownData} = this.subBaseData;
            let reliability = 'HIGH';
            
            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setTextColor(29, 29, 29);
            doc.setFontSize(13);
            const unknownText = doc.splitTextToSize(this.$t('유효 응답률', this.report_lang), this.pageContentWidth);
            const unknownTextDim = doc.getTextDimensions(unknownText);
            const unknownTextXPos = chartXPos + (100 - unknownTextDim.w) / 2;
            doc.text(unknownText, unknownTextXPos, position_y + 12);
            
            const guideTextBoxYPos = position_y + 6;
            const guideTextBoxWidth = this.pageContentWidth - rightContentXPos + 29;

            this.setFont(doc, 'table_td', null, 'semibold');
            doc.setTextColor(29, 29, 29);
            doc.setFontSize(15);
            // 영문은 섹션 제목과 같은 용어(평가 신뢰도 = Data Quality)를 쓴다.
            // '신뢰도' 키는 AI 평가 화면(AI 신뢰도)도 쓰므로 전역 영문을 'Data Quality' 로 바꾸면 그 화면이 틀어진다.
            const reliabilityLabel = this.report_lang === 'en' ? this.$t('평가 신뢰도', 'en') : this.$t('신뢰도', this.report_lang);
            const reliabilityText = doc.splitTextToSize(reliabilityLabel + ':', this.pageContentWidth);
            const reliabilityTextYPos = guideTextBoxYPos + 20;
            const reliabilityTextXPos = rightContentXPos + 8;
            doc.text(reliabilityText, reliabilityTextXPos, reliabilityTextYPos);
            
            doc.setTextColor(this.report_colors.compliance);
            if (unknownData.validRate < 60) {
                reliability = 'LOW';
                doc.setTextColor(this.report_colors.nonCompliance);
            } else if (unknownData.validRate < 80 && unknownData.validRate >= 60) {
                reliability = 'MID';
                doc.setTextColor(this.report_colors.warning);
            }
            const reliabilityDataXPos = reliabilityTextXPos + doc.getTextDimensions(reliabilityText).w + 4;
            doc.text(reliability, reliabilityDataXPos, reliabilityTextYPos);
            
            const reliabilityGuideText = '* HIGH: 100 ~ 80%, MID: 80 ~ 60%, LOW: 60% ~ 0%';
            const reliabilityGuideTextYPos = reliabilityTextYPos + 10;
            this.setFont(doc, 'table_td', null, 'normal');
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(9);
            doc.text(reliabilityGuideText, reliabilityTextXPos, reliabilityGuideTextYPos);
            
            doc.setTextColor(29, 29, 29);
            doc.setFontSize(11);
            const unknownGuideText = doc.splitTextToSize(summaryMsg.assessmentRateMsg(this.report_lang, this.companyInfo['회사명'], unknownData, reliability), this.pageContentWidth - reliabilityTextXPos + 22);
            doc.text(unknownGuideText, reliabilityTextXPos, reliabilityGuideTextYPos + 20);

            // 상자 테두리: 기본 116, 설명이 길어(영문) 넘칠 때만 글 높이에 맞춰 늘린다. 선만 그리므로 글 뒤에 그려도 같다.
            const guideTextBottom = reliabilityGuideTextYPos + 20 + doc.getTextDimensions(unknownGuideText).h;
            doc.setLineWidth(0.8);
            doc.setDrawColor(100, 100, 100);
            doc.rect(rightContentXPos, guideTextBoxYPos, guideTextBoxWidth, Math.max(116, guideTextBottom - guideTextBoxYPos + 4), 'S');

            const unKnownRate = await this.generateCodeSummaryPieChartInstance(
                [unknownData.validRate, 100 - unknownData.validRate],
                {data1: this.report_colors.compliance, data2: this.report_colors.gray},
            );
            await waitTime(100);
            const unknownChartPosY = position_y + 8 + 12;
            doc.addImage(unKnownRate, 'PNG', chartXPos, unknownChartPosY, 100, 100, undefined, 'FAST');
            this.codeDoughnutChartInstance?.destroy();
            this.codeDoughnutChartInstance = null;
            
            this.setFont(doc, 'table_td', null, 'bold');
            doc.setTextColor(29, 29, 29);
            doc.setFontSize(24);
            const unknownRateText = doc.splitTextToSize(unknownData.validRate.toString() + '%', this.pageContentWidth);
            const unknownRateTextDim = doc.getTextDimensions(unknownRateText);
            const unknownRateTextPosX = chartXPos + (100 - unknownRateTextDim.w) / 2;
            const unknownRateTextPosY = unknownChartPosY + 100 - (100 - unknownRateTextDim.h) / 2;
            doc.text(unknownRateText, unknownRateTextPosX, unknownRateTextPosY);
            
            return position_y;
        },
        generateCodeSummaryPieChartInstance(codeComplianceData, colors) {
            return new Promise((resolve) => {
                const _scope1_data = {
                    datasets: [{
                        data: codeComplianceData,
                        fill: true,
                        backgroundColor: [
                            colors.data1,
                            colors.data2
                        ],
                        borderColor: [
                            colors.data1,
                            colors.data2
                        ],
                    }]
                };
                
                const _common_option = {
                    global: {
                        aspectRatio: 5 / 5,
                        responsive: false,
                        maintainAspectRatio: false,
                        responsiveAnimationDuration: 0,
                        animation: {
                            duration: 0,
                            onComplete: function () {
                                resolve(this.toBase64Image('image/jpg', 1));
                            }
                        }
                    },
                    plugins: {
                        datalabels: {display: false}
                    },
                };
                
                const _scope1_area_option = {
                    ..._common_option.global,
                    elements: {line: {borderWidth: 1}},
                    scales: {},
                    plugins: _common_option.plugins,
                    cutout: '77.5%'
                };
                
                if (!this.codeDoughnutChartInstance) {
                    this.codeDoughnutChartInstance = this.$chartjs.createChart(
                        document.getElementById('code-summary-pie-chart'),
                        {
                            type: 'doughnut',
                            data: _scope1_data,
                            options: _scope1_area_option
                        }
                    );
                } else {
                    this.$chartjs.updateChart(this.codeDoughnutChartInstance, _scope1_data, _scope1_area_option);
                }
            });
            
            // return this.drawCategory1PieChartImage()
        },
        drawCategory1PieChartImage() {
            return this.codeDoughnutChartInstance.toBase64Image('image/jpg', 1);
        },
        addDocPage(doc, alreadyAdded = false) {
            if (!alreadyAdded)
                doc.addPage();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            doc.addImage(background_texture_img, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
        },
        generateCover_supply(doc) {
            try {
                // 페이지 너비,높이
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                
                const maxWidth = pageWidth - 20; // 페이지의 여백을 고려한 너비
                const maxHeight = pageHeight - 20; // 페이지의 여백을 고려한 높이
                
                // 표지 색상 설정
                doc.setFillColor(this.report_colors.primary); // RGB 값
                doc.rect(0, 0, pageWidth, pageHeight, 'F'); // 'F'는 fill
                
                const cover_logo_position_y = 40;

                const qesgLogoSize = {
                    width: 3747 / 45, height: 942 / 45
                };

                // QESG 로고 추가
                doc.addImage(QESGLogoWhite, 'png', 333, cover_logo_position_y - 5, qesgLogoSize.width, qesgLogoSize.height);
                
                
                let _text_position_y = (maxHeight / 3) + (this.jsPDFOpt.margin.top * 1.25);
                let _text_position_x = this.jsPDFOpt.margin.left;
                
                const additional_info_position_y = _text_position_y - 40;
                // "재평가 결과보고서" 문구 추가
                if (this.is_reassessment) {
                    this.setFont(doc, 'title');
                    doc.setTextColor('#FFFFFF');
                    const _reassessment_report_title = doc.splitTextToSize(`[${this.$t('재평가 결과보고서', this.report_lang)}]`, this.pageContentWidth);
                    doc.text(_reassessment_report_title, _text_position_x, additional_info_position_y, {align: 'left'});
                }
                
                // 심화평가 문구 추가 ("재평가 결과보고서" 문구가 추가되면 심화평가 문구는 추가 안됨)
                if (!this.is_reassessment && ((this.assessmentType && this.assessmentType === 'OSIS') || (!this.assessmentType && this.reportData.info.service_type === '심화'))) {
                    this.setFont(doc, 'title');
                    doc.setTextColor('#FFFFFF');
                    const _osis_report_title = doc.splitTextToSize(`[${this.$t('실사평가 결과보고서', this.report_lang)}]`, this.pageContentWidth);
                    doc.text(_osis_report_title, _text_position_x, additional_info_position_y, {align: 'left'});
                }
                
                // 보고서 이름 추가
                this.setFont(doc, 'cover_name_lg');
                doc.setTextColor('#FFFFFF');
                const [_report_title_width, _report_title_height] = [doc.getTextDimensions(this.cover_title).w, doc.getTextDimensions(this.cover_title).h];
                
                
                doc.text(this.cover_title, _text_position_x, _text_position_y, {});
                
                // 보고서 기업명 추가
                if (this.report_lang === 'ca' && this.isChineseOrHanzi(this.companyInfo['회사명'])) {
                    this.setFont(doc, 'cover_company_name_lg', this.jsPDFOpt.fontFamily_ca);
                } else {
                    this.setFont(doc, 'cover_company_name_lg', this.jsPDFOpt.fontFamily);
                }
                
                const _company_name = doc.splitTextToSize(this.companyInfo['회사명'], this.pageContentWidth);
                _text_position_y += _report_title_height + (this.jsPDFOpt.margin.top * 0.75);
                doc.setTextColor('#FFFFFF');
                doc.text(_company_name, _text_position_x, _text_position_y);
                
                // disclaimer 추가
                /**
                 * 969696
                 */
                this.setFont(doc, 'title_sm', this.jsPDFOpt.fontFamily);
                doc.setTextColor('#FFFFFF');
                doc.text('Disclaimer', 30, maxHeight - 37, {});
                
                doc.setDrawColor('#FFFFFF'); // RGB 값
                doc.setLineWidth(0.5);                // 굵은 선
                doc.line(28, maxHeight - 32, this.report_lang === 'en' ? 300 : 280, maxHeight - 32); // 좌측 20~우측 190, y=40 위치에 가로선
                
                const texts = [
                    `- ${this.$t('result_report_disclaimer.text1', this.report_lang)}`,
                    `  ${this.$t('result_report_disclaimer.text2', this.report_lang)}`,
                ];
                
                // 각 줄별로 출력 (들여쓰기 반영)
                const lineHeight = 9;
                const leftMargin = 30;
                let y = maxHeight - 22; // 시작 위치
                texts.forEach((line) => {

                    this.setFont(doc, 'footnote', this.jsPDFOpt.fontFamily);
                    doc.setTextColor('#FFFFFF');
                    // 영문은 한 줄이 쪽 폭을 넘을 수 있어 그때만 줄바꿈한다 (폭 안이면 들여쓰기 공백까지 기존 그대로)
                    const lineMaxWidth = pageWidth - leftMargin * 2;
                    const wrapped = doc.getTextWidth(line) > lineMaxWidth ? doc.splitTextToSize(line, lineMaxWidth) : [line];
                    doc.text(wrapped, leftMargin, y);
                    y += lineHeight * wrapped.length;
                });
            } catch (error) {
                console.log(error);
            }
        },
    }
};
